import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { DataTypeModelKey } from "app/models/dataType.model";
import { ProjectDeviceModel } from "app/models/projectDevice.model";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { ApiService } from "./api.service";

@Injectable()
export class ProjectDeviceService extends ApiService {
    private readonly nestedDataTypeAliases: Record<string, string> = {
        soundPressureLevel: DataTypeModelKey.soundPressureLevel,
        soundPressureLevelC: DataTypeModelKey.soundPressureLevelC,
        soundPressureLevel1Minute: DataTypeModelKey.soundPressureLevel1Minute,
        soundPressureLevel1MinuteSensor2: DataTypeModelKey.soundPressureLevel1MinuteSensor2,
        soundPressureLevelC1Minute: DataTypeModelKey.soundPressureLevelC1Minute,
        soundPressureLevelCMinusA: DataTypeModelKey.soundPressureLevelCMinusA,
        rolling5s: DataTypeModelKey.rolling5s,
        rolling5sC: DataTypeModelKey.rolling5sC,
        rolling5: DataTypeModelKey.rolling5,
        rolling5C: DataTypeModelKey.rolling5C,
        rolling30: DataTypeModelKey.rolling30,
        rolling30C: DataTypeModelKey.rolling30C,
        battery: DataTypeModelKey.battery,
        overload: DataTypeModelKey.overload,
        underrange: DataTypeModelKey.underrange,
        powerSupplyStatus: DataTypeModelKey.powerSupplyStatus,
        wifiSignalLevel: DataTypeModelKey.wifiSignalLevel,
        cellullarSignalLevel: DataTypeModelKey.cellullarSignalLevel,
        temperature: DataTypeModelKey.temperature,
        spectrum13Octave: DataTypeModelKey.spectrum13Octave,
        spectrum13Octave1Minute: DataTypeModelKey.spectrum13Octave1Minute,
        freeStorage: DataTypeModelKey.freeStorage,
    };

    constructor(protected http: HttpClient) {
        super(http);
        this.apiEndpoint = "project-device";
    }

    getAll(projectId: string): Observable<ProjectDeviceModel[]> {
        const url = `${projectId}/project-device`;
        const apiEndpoint = "project";
        return this.http
            .get<ProjectDeviceModel[]>(this.getUrl(url, apiEndpoint))
            .pipe(map((array) => array.map((pD) => this.mapProjectDevice(pD))));
    }

    create(projectId: string, projectDevice: ProjectDeviceModel) {
        const url = `${projectId}/project-device`;
        const apiEndpoint = "project";
        return this.http.post(this.getUrl(url, apiEndpoint), projectDevice).pipe(map((t: ProjectDeviceModel) => this.mapProjectDevice(t)));
    }

    edit(projectId: string, projectDevice: ProjectDeviceModel) {
        const url = `${projectId}/project-device`;
        const apiEndpoint = "project";
        if (projectDevice.settings?.lf010) {
            delete projectDevice.settings.lf010.devicePeriods;
            delete projectDevice.settings.lf010.limitHistory;
            delete projectDevice.settings.lf010.managedAlarmIds;
        }
        delete projectDevice.project;
        delete projectDevice.baseDevice;
        return this.http.put(this.getUrl(url, apiEndpoint), projectDevice).pipe(map((t: ProjectDeviceModel) => this.mapProjectDevice(t)));
    }

    get(projectDeviceId: string): Observable<ProjectDeviceModel> {
        const url = `${projectDeviceId}`;
        return this.http.get<ProjectDeviceModel>(this.getUrl(url)).pipe(
            map((projectDevice) => {
                return this.mapProjectDevice(projectDevice);
            }),
        );
    }

    delete(projectDeviceId: string) {
        const url = `${projectDeviceId}`;
        return this.http.delete(this.getUrl(url));
    }

    activate(projectDeviceId: string): Observable<any> {
        const url = `${projectDeviceId}/activate`;
        return this.http.post(this.getUrl(url), {});
    }

    deactivate(projectDeviceId: string): Observable<any> {
        const url = `${projectDeviceId}/deactivate`;
        return this.http.post(this.getUrl(url), {});
    }

    reset(projectDeviceId: string): Observable<any> {
        const url = `${projectDeviceId}/reset`;
        return this.http.post(this.getUrl(url), {});
    }

    syncLF010Alarm(projectDeviceId: string): Observable<ProjectDeviceModel> {
        const url = `${projectDeviceId}/lf010/sync-alarm`;
        return this.http.post<ProjectDeviceModel>(this.getUrl(url), {}).pipe(map((projectDevice) => this.mapProjectDevice(projectDevice)));
    }

    addImage(projectDeviceId: string, image: any) {
        const url = `${projectDeviceId}/image`;
        const formData: FormData = new FormData();
        formData.append("image", image, image.name);
        return this.http.post(this.getUrl(url), formData);
    }

    removeImage(projectDeviceId: string) {
        const url = `${projectDeviceId}/image`;
        return this.http.delete(this.getUrl(url));
    }

    addFile(projectDeviceId: string, file: any) {
        const url = `${projectDeviceId}/file`;
        const formData: FormData = new FormData();
        formData.append("file", file, file.name);
        return this.http.post(this.getUrl(url), formData);
    }

    private mapProjectDevice(projectDevice: ProjectDeviceModel): ProjectDeviceModel {
        const mapped = Object.assign(new ProjectDeviceModel(), projectDevice);
        mapped.lastData = this.normalizeDataTypeMap(mapped.lastData);
        mapped.dataAggregation = this.normalizeDataTypeMap(mapped.dataAggregation);
        if (mapped.operation?.requestedAt) {
            mapped.operation.requestedAt = new Date(mapped.operation.requestedAt);
        }
        if (mapped.operation?.startedAt) {
            mapped.operation.startedAt = new Date(mapped.operation.startedAt);
        }
        if (mapped.operation?.finishedAt) {
            mapped.operation.finishedAt = new Date(mapped.operation.finishedAt);
        }
        if (mapped.settings?.scheduling?.restart?.lastTriggeredAt) {
            mapped.settings.scheduling.restart.lastTriggeredAt = new Date(mapped.settings.scheduling.restart.lastTriggeredAt);
        }
        if (mapped.settings?.lf010?.limitHistory?.length) {
            mapped.settings.lf010.limitHistory = mapped.settings.lf010.limitHistory.map((entry) => ({
                ...entry,
                changedAt: new Date(entry.changedAt),
            }));
        }
        return mapped;
    }

    private normalizeDataTypeMap<T extends Record<string, any>>(dataMap?: T): T {
        if (!dataMap) {
            return dataMap;
        }

        const normalized = { ...dataMap } as Record<string, any>;

        for (const [modelKey, apiKey] of Object.entries(this.nestedDataTypeAliases)) {
            if (normalized[modelKey] === undefined && normalized[apiKey] !== undefined) {
                normalized[modelKey] = normalized[apiKey];
            }
            if (normalized[apiKey] === undefined && normalized[modelKey] !== undefined) {
                normalized[apiKey] = normalized[modelKey];
            }
        }

        return normalized as T;
    }
}
